/**
 * CR444: FlyerSnippetDetailsBlock — UNCONDITIONAL rendering with mount sentinel.
 * CR450: Passes interaction config from block.config to FlyerPreview.
 * CR479: "View snippet evidence" affordance — scrolls to FlyerEvidenceSection.
 * If enabled: always renders image OR placeholder on back surface. Never blank.
 */

import React, { useCallback } from "react";
import { FileImage, ArrowDown } from "lucide-react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { FlyerPreview } from "@/components/deals/FlyerPreview";
import type { FlyerSnippetInteractionConfig } from "@/components/deals/FlyerPreview";

export const FlyerSnippetDetailsBlock: React.FC<DealCardBlockProps<Deal>> = ({ deal, block, context }) => {
  // CR479: Hooks must be called before any early return
  const dealAny = deal as any;
  const dealId = dealAny?.id ?? "";

  const handleViewEvidence = useCallback(() => {
    const el = document.getElementById(`flyer-evidence-${dealId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("bg-primary/10");
    setTimeout(() => el.classList.remove("bg-primary/10"), 1200);
  }, [dealId]);

  if (!block?.enabled) return null;

  // CR451: Read interaction config from block.config, then context.flyerInteraction as fallback
  const blockCfg = (block as any)?.config ?? {};
  const ctxFlyer = context?.flyerInteraction ?? {};
  
  const interactionConfig: FlyerSnippetInteractionConfig = {
    interaction: blockCfg.interaction ?? ctxFlyer.interaction ?? "modal",
    modalTitle: blockCfg.modalTitle ?? ctxFlyer.modalTitle ?? "Flyer preview",
    allowBackdropClose: blockCfg.allowBackdropClose ?? ctxFlyer.allowBackdropClose ?? true,
  };

  // CR479: Determine if snippet evidence is available for the link
  const flyerArtifact = dealAny?.flyer_artifact ?? dealAny?.flyerArtifact ?? null;
  const hasSnippetEvidence = !!(
    flyerArtifact?.page_image_path ||
    flyerArtifact?.thumbnail_storage_path ||
    flyerArtifact?.snippet_asset_path ||
    flyerArtifact?.snippet_storage_path_norm
  );

  return (
    <div
      data-block="flyer_snippet_details"
      data-deal-id={dealId}
      data-dedupe-key={(deal as any)?.dedupe_key ?? ""}
      className="w-full"
    >
      <div className="flex items-center justify-between gap-1.5 mb-2">
        <div className="flex items-center gap-1.5">
          <FileImage className="w-4 h-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold text-foreground">Flyer Snippet</h4>
        </div>
        {hasSnippetEvidence && dealId && (
          <button
            type="button"
            onClick={handleViewEvidence}
            className="inline-flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 hover:underline transition-colors"
          >
            <ArrowDown className="w-3 h-3" />
            View snippet evidence
          </button>
        )}
      </div>
      <FlyerPreview deal={deal as any} interactionConfig={interactionConfig} />
    </div>
  );
};

export default FlyerSnippetDetailsBlock;
